import React, { useState } from 'react';
import Avatar from '../components/ui/Avatar';
import Icon   from '../components/ui/Icon';
import Button from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { COMPLETED_REQUESTS } from '../data/mockData';

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-4 py-3.5 border-b border-surface-500 last:border-0">
    <div className="w-9 h-9 rounded-xl bg-surface-700 flex items-center justify-center flex-shrink-0 border border-surface-500">
      <Icon name={icon} size={16} className="text-surface-200" />
    </div>
    <div className="flex-1">
      <p className="font-dm text-xs text-surface-300 uppercase tracking-wider">{label}</p>
      <p className="font-dm text-sm text-slate-800 font-medium mt-0.5">{value}</p>
    </div>
    <Icon name="arrow" size={14} className="text-surface-300" />
  </div>
);

const ProfilePage = () => {
  const { user, logout } = useAuth();
  const [editing, setEditing] = useState(false);

  const lsUser = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  }, []);

  const name         = user?.name         || lsUser?.name         || [lsUser?.first_name, lsUser?.last_name].filter(Boolean).join(' ') || 'User';
  const email        = user?.email        || lsUser?.email        || '';
  const phone        = user?.phone_number || lsUser?.phone_number || user?.phone || lsUser?.phone || '';
  const countryCode  = user?.country_code || lsUser?.country_code || '';
  const fullPhone    = countryCode ? `${countryCode} ${phone}` : phone;
  const initials     = name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'U';

  const avgRating =
    COMPLETED_REQUESTS.reduce((a, r) => a + r.rating, 0) / COMPLETED_REQUESTS.length;

  return (
    <div className="page-scroll">
      <div className="pb-10">
        {/* Hero */}
        <div className="px-5 pt-5 pb-4">
          <div
            className="rounded-3xl p-6 relative overflow-hidden animate-fadeUp card-shadow"
            style={{
              background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 40%, #eef2ff 100%)',
              border: '1px solid rgba(99,102,241,0.18)',
              animationFillMode: 'forwards',
            }}
          >
            <div className="noise-overlay rounded-3xl" />
            <div
              className="absolute -top-8 -right-8 w-36 h-36 rounded-full opacity-15 pointer-events-none"
              style={{ background: 'radial-gradient(circle, #6366f1, transparent 70%)' }}
            />

            <div className="relative z-10 flex flex-col items-center text-center">
              <div className="relative mb-4">
                <Avatar initials={user?.avatar || initials} size="xl" ring />
                <button className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center border-2 border-white press-scale">
                  <Icon name="plus" size={13} className="text-white" />
                </button>
              </div>
              <h2 className="font-syne font-extrabold text-slate-800 text-xl">{name}</h2>
              <p className="font-dm text-surface-200 text-xs mt-1">{email}</p>

              <div className="flex items-center gap-4 mt-5">
                {[
                  { val: '24',    label: 'Requests' },
                  { val: avgRating.toFixed(1), label: 'Avg Rating' },
                  { val: '₹6.4k', label: 'Total Spent' },
                ].map((s) => (
                  <div key={s.label} className="flex flex-col items-center gap-0.5">
                    <p className="font-syne font-extrabold text-slate-800 text-lg">{s.val}</p>
                    <p className="font-dm text-xs text-slate-500">{s.label}</p>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 border border-brand-200">
                 <span className="font-dm text-xs text-slate-500">Member Since {user?.memberSince || lsUser?.created_at?.slice(0, 4) || '2024'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Personal Info */}
        <div className="px-5 mb-4">
          <div
            className="bg-surface-800 rounded-2xl px-4 card-shadow border border-surface-500 animate-fadeUp s2"
            style={{ animationFillMode: 'forwards', opacity: 0 }}
          >
            <InfoRow icon="user"  label="Full Name" value={name}      />
            <InfoRow icon="mail"  label="Email"     value={email}     />
            <InfoRow icon="phone" label="Phone"     value={fullPhone} />
          </div>
        </div>

        {/* Settings */}
        <div className="px-5 mb-4">
          <h3 className="font-syne font-bold text-slate-800 text-sm mb-3 animate-fadeUp s3" style={{ animationFillMode: 'forwards', opacity: 0 }}>
            Settings & More
          </h3>
          <div
            className="bg-surface-800 rounded-2xl px-4 card-shadow border border-surface-500 animate-fadeUp s3"
            style={{ animationFillMode: 'forwards', opacity: 0 }}
          >
            {[
              { icon: 'bell',     label: 'Notifications',   sub: 'Manage alerts'    },
              { icon: 'wallet',   label: 'Payment Methods', sub: 'Cards & UPI'      },
              { icon: 'help',     label: 'Help & Support',  sub: 'FAQs & live chat' },
              { icon: 'settings', label: 'App Settings',    sub: 'Theme, language'  },
            ].map((item) => (
              <button
                key={item.label}
                className="w-full flex items-center gap-4 py-3.5 border-b border-surface-500 last:border-0 text-left press-scale"
              >
                <div className="w-9 h-9 rounded-xl bg-surface-700 flex items-center justify-center flex-shrink-0 border border-surface-500">
                  <Icon name={item.icon} size={16} className="text-surface-200" />
                </div>
                <div className="flex-1">
                  <p className="font-dm text-sm text-slate-800 font-medium">{item.label}</p>
                  <p className="font-dm text-xs text-surface-200">{item.sub}</p>
                </div>
                <Icon name="arrow" size={14} className="text-surface-300" />
              </button>
            ))}
          </div>
        </div>

        {/* Sign out */}
        <div className="px-5 animate-fadeUp s5" style={{ animationFillMode: 'forwards', opacity: 0 }}>
          <Button variant="danger" size="lg" onClick={logout}>
            <Icon name="logout" size={16} />
            Sign Out
          </Button>

          <p className="font-dm text-xs text-surface-300 text-center mt-4">
            HostAssist v1.0.0 • Made with ❤️ in India
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
